import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import SpreadsheetViewer from './SpreadsheetViewer';

// Fetches a PDF as a blob and renders it via an object URL, bypassing
// X-Frame-Options / CSP frame restrictions set by the API server.
const PdfViewer = ({ artifactUrl, filename }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let objectUrl = null;
    fetch(artifactUrl)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then(blob => {
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      })
      .catch(err => setError(err.message));
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [artifactUrl]);

  if (error) {
    return (
      <div style={{ color: 'white', padding: '40px', textAlign: 'center' }}>
        <p style={{ marginBottom: '12px' }}>Could not display PDF inline.</p>
        <a
          href={artifactUrl}
          download={filename}
          style={{ color: '#7dd3fc', fontSize: '16px' }}
        >
          ⬇ Download {filename}
        </a>
      </div>
    );
  }

  if (!blobUrl) {
    return (
      <div style={{ color: 'rgba(255,255,255,0.6)', padding: '40px', textAlign: 'center' }}>
        Loading PDF…
      </div>
    );
  }

  return (
    <iframe
      src={blobUrl}
      title={filename}
      style={{ width: '100%', height: '100%', border: 'none' }}
    />
  );
};

// Helper: extract YouTube video ID from any YouTube URL format
function extractYouTubeId(url) {
  if (!url) return null;
  const m = String(url).match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
}

// API URL configuration - uses relative URL in production (nginx proxies to backend)
const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' && window.location.port === '3002'
    ? 'http://localhost:3000/api'
    : '/api');

const ArtifactViewer = ({ artifacts, currentIndex, onClose, onPrev, onNext, amplifierId, ampUrl }) => {
  const [copied, setCopied] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Track which artifact IDs have their full image loaded in the browser cache.
  // Using a ref (not state) so preload callbacks don't trigger re-renders.
  const loadedIds = useRef(new Set());
  // Force re-render when the current image finishes loading.
  const [, forceUpdate] = useState(0);
  const [imageLoadError, setImageLoadError] = useState(false);

  // Reset zoom/pan and error state when switching images
  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setImageLoadError(false);
  }, [currentIndex]);

  // Lazy-load: preload ±1 neighbour images once the current image is ready.
  useEffect(() => {
    if (!artifacts || artifacts.length === 0) return;
    const artifact = artifacts[currentIndex];
    if (!artifact || !artifact.mime_type?.startsWith('image/')) return;

    // Preload neighbours into browser cache (fire-and-forget; no state updates).
    // Do NOT add to loadedIds here — only the actual DOM <img> onLoad should
    // control the placeholder→full swap, otherwise the placeholder is skipped
    // before the DOM image has rendered.
    [-1, 1].forEach(offset => {
      const neighbour = artifacts[currentIndex + offset];
      if (!neighbour || !neighbour.mime_type?.startsWith('image/')) return;
      const neighbourKey = neighbour.artifact_id || neighbour._url;
      if (!neighbourKey || loadedIds.current.has(neighbourKey)) return;
      const pre = new window.Image();
      pre.src = neighbour._url || `${API_URL}/amplifier_artifacts/${neighbour.artifact_id}`;
    });
  }, [currentIndex, artifacts]);

  // Called by the full-size <img> onLoad — reliably swaps from placeholder.
  const handleFullImageLoad = useCallback(() => {
    if (!artifacts || artifacts.length === 0) return;
    const artifact = artifacts[currentIndex];
    if (!artifact) return;
    loadedIds.current.add(artifact.artifact_id || artifact._url);
    forceUpdate(n => n + 1);
  }, [currentIndex, artifacts]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === '+' || e.key === '=') setZoom(z => Math.min(z + 0.25, 5));
      if (e.key === '-') setZoom(z => Math.max(z - 0.25, 0.25));
      if (e.key === '0') { setZoom(1); setPan({ x: 0, y: 0 }); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onPrev, onNext]);
  
  const handleZoomIn = useCallback((e) => {
    e.stopPropagation();
    setZoom(z => Math.min(z + 0.25, 5));
  }, []);
  
  const handleZoomOut = useCallback((e) => {
    e.stopPropagation();
    setZoom(z => Math.max(z - 0.25, 0.25));
  }, []);
  
  const handleResetZoom = useCallback((e) => {
    e.stopPropagation();
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom(z => Math.max(0.25, Math.min(5, z + delta)));
  }, []);

  const handleMouseDown = useCallback((e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  }, [zoom, pan]);

  const handleMouseMove = useCallback((e) => {
    if (isDragging && zoom > 1) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  }, [isDragging, zoom, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);
  
  if (!artifacts || artifacts.length === 0 || currentIndex < 0) return null;

  const artifact = artifacts[currentIndex];
  const artifactUrl = artifact._url || `${API_URL}/amplifier_artifacts/${artifact.artifact_id}`;
  
  // Build shareable URL for this specific image (prefer SEO slug URL)
  const basePath = ampUrl || (amplifierId ? `/amplifier-components/${amplifierId}` : null);
  const shareUrl = basePath 
    ? `${basePath}?image=${currentIndex}`
    : null;
  
  const handleCopyLink = async (e) => {
    e.stopPropagation();
    if (!shareUrl) return;
    
    const fullUrl = `${window.location.origin}${shareUrl}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = fullUrl;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (e) {
        console.error('Copy failed:', e);
      }
      document.body.removeChild(textArea);
    }
  };

  const SPREADSHEET_MIME_TYPES = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'text/csv',
    'application/vnd.oasis.opendocument.spreadsheet',
  ];
  const isImage = artifact.mime_type && artifact.mime_type.startsWith('image/');
  const isSvg = artifact.mime_type === 'image/svg+xml';
  const isSpreadsheet = SPREADSHEET_MIME_TYPES.includes(artifact.mime_type);
  const isYouTube = artifact.artifact_type === 'youtube' && artifact.external_url;
  const isAudio = artifact.mime_type && artifact.mime_type.startsWith('audio/');
  const youtubeVideoId = isYouTube ? extractYouTubeId(artifact.external_url) : null;
  const artifactKey = artifact.artifact_id || artifact._url;
  const isLoaded = isImage && loadedIds.current.has(artifactKey);
  const thumbnailUrl = artifact._url ? null : `${API_URL}/amplifier_artifacts/${artifact.artifact_id}/thumbnail`;

  let content;
  if (isImage) {
    const imgStyle = {
      maxHeight: '100%',
      maxWidth: '100%',
      objectFit: 'contain',
      transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
      transition: isDragging ? 'none' : 'transform 0.1s ease-out',
      userSelect: 'none',
    };
    content = (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          position: 'relative',
          cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
        }}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {/* Thumbnail shown immediately as placeholder while full image loads.
             Skip for SVG: the full SVG loads fast and the JPEG thumbnail would
             be a rasterized downgrade of a vector file. */}
        {!isLoaded && thumbnailUrl && !isSvg && (
          <img
            src={thumbnailUrl}
            alt={artifact.filename}
            draggable={false}
            style={{ ...imgStyle, filter: 'blur(4px)', opacity: 0.6 }}
          />
        )}
        {/* Full-resolution image — invisible until loaded, then swaps in */}
        <img
          src={artifactUrl}
          alt={artifact.filename}
          draggable={false}
          onLoad={handleFullImageLoad}
          onError={() => { setImageLoadError(true); forceUpdate(n => n + 1); }}
          style={{
            ...imgStyle,
            position: isLoaded ? 'static' : 'absolute',
            opacity: isLoaded ? 1 : 0,
          }}
        />
        {/* Loading / error indicator */}
        {!isLoaded && (
          <div style={{
            position: 'absolute',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            background: imageLoadError ? 'rgba(180,0,0,0.7)' : 'rgba(0,0,0,0.6)',
            color: 'rgba(255,255,255,0.85)',
            padding: '6px 16px',
            borderRadius: 20,
            fontSize: 13,
            pointerEvents: 'none',
          }}>
            {imageLoadError ? 'Failed to load image' : 'Loading full image…'}
          </div>
        )}
      </div>
    );
  } else if (artifact.mime_type === 'application/pdf') {
    content = <PdfViewer artifactUrl={artifactUrl} filename={artifact.filename} />;
  } else if (isSpreadsheet) {
    content = <SpreadsheetViewer artifactUrl={artifactUrl} filename={artifact.filename} />;
  } else if (isYouTube && youtubeVideoId) {
    content = (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
        <iframe
          src={`https://www.youtube.com/embed/${youtubeVideoId}`}
          title={artifact.filename}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{ width: '100%', maxWidth: '900px', aspectRatio: '16/9', border: 'none' }}
        />
      </div>
    );
  } else if (isAudio) {
    content = (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '16px' }}>
        <span style={{ fontSize: '48px' }}>🎵</span>
        <audio
          controls
          src={artifactUrl}
          style={{ width: '100%', maxWidth: '500px' }}
        />
        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>{artifact.filename}</div>
      </div>
    );
  } else {
    content = (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <a href={artifactUrl} download={artifact.filename} style={{ color: 'white', fontSize: '18px' }}>
          Download {artifact.filename}
        </a>
      </div>
    );
  }

  // Use ReactDOM.createPortal to render outside the component tree
  // This ensures the modal is not affected by parent stacking contexts
  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.95)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 99999
      }}
    >
      {/* Top toolbar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 20px',
          background: 'rgba(0,0,0,0.5)',
          borderBottom: '1px solid rgba(255,255,255,0.1)'
        }}
      >
        {/* Left side - image counter */}
        <div style={{ color: 'white', fontSize: '14px', fontWeight: '500' }}>
          {currentIndex + 1} / {artifacts.length}
        </div>
        
        {/* Center - zoom controls (only for images) */}
        {isImage && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={handleZoomOut}
              style={{
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid #e9ecef',
                borderRadius: '4px',
                color: 'white',
                padding: '6px 12px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
              title="Zoom Out (-)"
            >
              −
            </button>
            <button
              onClick={handleResetZoom}
              style={{
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid #e9ecef',
                borderRadius: '4px',
                color: 'white',
                padding: '6px 12px',
                cursor: 'pointer',
                fontSize: '14px',
                minWidth: '60px'
              }}
              title="Reset Zoom (0)"
            >
              {Math.round(zoom * 100)}%
            </button>
            <button
              onClick={handleZoomIn}
              style={{
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid #e9ecef',
                borderRadius: '4px',
                color: 'white',
                padding: '6px 12px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
              title="Zoom In (+)"
            >
              +
            </button>
          </div>
        )}
        
        {/* Right side - action buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {shareUrl && (
            <button
              onClick={handleCopyLink}
              style={{ 
                fontSize: 14, 
                background: copied ? '#48bb78' : '#2563eb', 
                border: 'none', 
                cursor: 'pointer',
                padding: '6px 12px',
                borderRadius: '4px',
                color: 'white',
                fontWeight: '500',
                transition: 'background 0.3s ease'
              }}
              title={copied ? 'Link copied!' : 'Copy shareable link'}
            >
              {copied ? '✓ Copied!' : 'Share'}
            </button>
          )}
          <button
            onClick={() => window.open(isYouTube ? artifact.external_url : artifactUrl, '_blank')}
            style={{ 
              fontSize: 14, 
              background: 'rgba(255,255,255,0.1)', 
              border: '1px solid #e9ecef', 
              cursor: 'pointer',
              padding: '6px 12px',
              borderRadius: '4px',
              color: 'white'
            }}
            title="Open in New Window"
          >
            ⧉ Open
          </button>
          <button
            onClick={onClose}
            style={{ 
              fontSize: 20, 
              background: 'rgba(255,100,100,0.2)', 
              border: '1px solid rgba(255,100,100,0.3)', 
              cursor: 'pointer',
              padding: '4px 12px',
              borderRadius: '4px',
              color: '#ff6b6b',
              fontWeight: 'bold'
            }}
            title="Close (Esc)"
          >
            ✕
          </button>
        </div>
      </div>
      
      {/* Main content area */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          padding: '20px 60px'
        }}
      >
        {/* Previous button */}
        <button
          onClick={onPrev}
          disabled={currentIndex === 0}
          style={{ 
            position: 'absolute', 
            left: '10px', 
            top: '50%', 
            transform: 'translateY(-50%)',
            fontSize: '36px', 
            background: currentIndex === 0 ? 'transparent' : 'rgba(255,255,255,0.1)', 
            border: 'none', 
            cursor: currentIndex === 0 ? 'default' : 'pointer',
            color: currentIndex === 0 ? 'rgba(255,255,255,0.2)' : 'white',
            padding: '20px 15px',
            borderRadius: '4px',
            transition: 'background 0.2s'
          }}
          title="Previous (←)"
        >
          ‹
        </button>
        
        {/* Content */}
        {content}
        
        {/* Next button */}
        <button
          onClick={onNext}
          disabled={currentIndex === artifacts.length - 1}
          style={{ 
            position: 'absolute', 
            right: '10px', 
            top: '50%', 
            transform: 'translateY(-50%)',
            fontSize: '36px', 
            background: currentIndex === artifacts.length - 1 ? 'transparent' : 'rgba(255,255,255,0.1)', 
            border: 'none', 
            cursor: currentIndex === artifacts.length - 1 ? 'default' : 'pointer',
            color: currentIndex === artifacts.length - 1 ? 'rgba(255,255,255,0.2)' : 'white',
            padding: '20px 15px',
            borderRadius: '4px',
            transition: 'background 0.2s'
          }}
          title="Next (→)"
        >
          ›
        </button>
      </div>
      
      {/* Bottom info bar */}
      <div
        style={{
          padding: '8px 20px',
          background: 'rgba(0,0,0,0.5)',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          textAlign: 'center',
          color: 'rgba(255,255,255,0.7)',
          fontSize: '13px'
        }}
      >
        {isYouTube ? artifact.external_url : artifact.filename}
        {isImage && zoom !== 1 && (
          <span style={{ marginLeft: '15px', color: 'rgba(255,255,255,0.5)' }}>
            • Scroll to zoom • Drag to pan • Press 0 to reset
          </span>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ArtifactViewer;